import { createSvgEl } from '@learnforge/demo-render';

export interface NumberLinePoint {
  id: string;
  value: number;
  label?: string;
  className: string;
  draggable: boolean;
}

export interface NumberLineArrow {
  from: number;
  to: number;
  className: string;
  flip?: boolean;
}

export interface NumberLineOptions {
  range: [number, number];
  ticks: { step: number; subSteps?: number };
  snap: 'integer' | 'half' | 'tenth' | 'off';
  points: readonly NumberLinePoint[];
  arrows?: readonly NumberLineArrow[];
  width: number;
  height: number;
  onPointMove?(id: string, value: number): void;
  onZoom?(range: [number, number]): void;
}

export interface NumberLineHandle {
  group: SVGGElement;
  update(opts: Partial<NumberLineOptions>): void;
  destroy(): void;
}

export function createNumberLine(
  parent: SVGGElement,
  initial: NumberLineOptions,
): NumberLineHandle {
  const group = createSvgEl('g', { class: 'arith-line' });
  parent.appendChild(group);
  let opts = { ...initial };

  function valueToX(v: number): number {
    const [a, b] = opts.range;
    return ((v - a) / (b - a)) * opts.width;
  }

  function render(): void {
    while (group.firstChild) group.removeChild(group.firstChild);
    const { range, ticks, points, arrows, width, height } = opts;
    const y = height / 2;
    const axis = createSvgEl('line', {
      class: 'arith-line-axis',
      x1: 0,
      y1: y,
      x2: width,
      y2: y,
    });
    group.appendChild(axis);
    const [a, b] = range;
    for (let v = Math.ceil(a / ticks.step) * ticks.step; v <= b; v += ticks.step) {
      const tx = valueToX(v);
      const tick = createSvgEl('line', {
        class: 'arith-line-tick',
        x1: tx,
        y1: y - 6,
        x2: tx,
        y2: y + 6,
      });
      group.appendChild(tick);
      const label = createSvgEl('text', {
        class: 'arith-line-tick-label',
        x: tx,
        y: y + 20,
        'text-anchor': 'middle',
      });
      label.textContent = String(v);
      group.appendChild(label);
    }
    if (arrows) {
      for (const arrow of arrows) {
        const x1 = valueToX(arrow.from);
        const x2 = valueToX(arrow.to);
        const path = createSvgEl('path', {
          class: `arith-line-arrow ${arrow.className}`,
          d: `M ${x1} ${y - 12} L ${x2} ${y - 12}`,
          'marker-end': 'url(#arith-arrowhead)',
        });
        group.appendChild(path);
      }
    }
    for (const p of points) {
      const px = valueToX(p.value);
      const circle = createSvgEl('circle', {
        class: `arith-line-point ${p.className}`,
        cx: px,
        cy: y,
        r: 6,
        'data-id': p.id,
      });
      group.appendChild(circle);
    }
  }

  render();

  return {
    group,
    update(next: Partial<NumberLineOptions>) {
      opts = { ...opts, ...next };
      render();
    },
    destroy() {
      if (group.parentNode) group.parentNode.removeChild(group);
    },
  };
}
